import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';

import { useModal } from 'reservations/base/providers/ModalProvider';
import { BookingDetails } from 'reservations/booking/components/BookingDetails';
import { useBookingEmail } from 'reservations/booking/providers/BookingProvider';
import { DinerAuthenticationInput } from 'reservations/dinerAccount/components/DinerAuthenticationInput';
import { VERIFY_YOUR_IDENTITY } from 'reservations/dinerAccount/constants';
import { useDinerAccount } from 'reservations/dinerAccount/hooks/useDinerAccount';

type ButtonVariant = 'primary' | 'secondary' | 'error';

interface ButtonProps {
  label: string;
  variant: ButtonVariant;
  disabled: boolean;
  onClick: () => void;
}

type BookingDetailsDisplay = 'hidden' | 'normal' | 'strikethrough';

export interface Props extends React.PropsWithChildren {
  header: string;
  subheader?: string;
  footer?: string;
  bookingDetailsDisplay?: BookingDetailsDisplay;
  primaryButton?: Partial<ButtonProps>;
  secondaryButton?: Partial<ButtonProps>;
  requireAuth?: boolean;
  dataTestId?: string;
}

export const BaseDrawer = ({
  header,
  subheader,
  footer,
  requireAuth = false,
  primaryButton,
  secondaryButton,
  bookingDetailsDisplay = 'hidden',
  dataTestId,
  children
}: Props) => {
  const email = useBookingEmail();
  const { isDinerAccountAuthenticated, setBeginAuthParams } = useDinerAccount();
  const { close } = useModal();
  const shouldAuthenticate = requireAuth && !isDinerAccountAuthenticated;
  const subheaderText = shouldAuthenticate ? VERIFY_YOUR_IDENTITY : subheader;

  const defaultPrimaryButtonProps = {
    // There's no "default" on click behavior for the primary button. This error throwing
    // is here to error out tests if primary on click prop is excluded
    onClick: () => {
      throw new Error('No on click added to primary button');
    },
    variant: 'primary' as ButtonVariant,
    disabled: false
  };
  const defaultSecondaryButtonProps = {
    onClick: close,
    variant: 'secondary' as ButtonVariant,
    disabled: false
  };

  useEffect(() => {
    if (shouldAuthenticate) {
      setBeginAuthParams(email);
    }
  }, [shouldAuthenticate]);

  return (
    <Box height="100%" padding="0 32px 24px" gap="24px">
      <Box height="64px" />
      <Typography variant="h2" data-testid={dataTestId}>
        {header}
      </Typography>
      {!!subheaderText && (
        <Typography mt="22px" whiteSpace="pre-wrap">
          {subheaderText}
        </Typography>
      )}
      <Box mt="24px">
        {shouldAuthenticate ? (
          <DinerAuthenticationInput hideHeader />
        ) : (
          <Grid container item direction="column" gap="24px">
            {bookingDetailsDisplay !== 'hidden' && (
              <BookingDetails
                showStrikeThrough={bookingDetailsDisplay === 'strikethrough'}
              />
            )}
            {!!children && <Grid item>{children}</Grid>}
            <Grid container gap="16px">
              {primaryButton && (
                <Button
                  {...defaultPrimaryButtonProps}
                  {...primaryButton}
                  size="fullWidth"
                >
                  {primaryButton?.label ?? 'SAVE'}
                </Button>
              )}
              {secondaryButton && (
                <Button
                  {...defaultSecondaryButtonProps}
                  {...secondaryButton}
                  size="fullWidth"
                >
                  {secondaryButton?.label ?? 'NEVER MIND'}
                </Button>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
      <Box mt="2rem" mb="6rem">
        {footer && (
          <Box>
            <Typography variant="caption" data-testid="footer">
              {footer}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
